import Image from 'next/image';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { logError } from '../../services/log.service';
import { formatDate, getImageSrcWithFallback } from '../../shared/useUtils';
import { BlogPostPageStyle } from '../../styles/components/blog';

const BlogPostSingle = (props: any) => {
  const shareBlogPost = async () => {
    const data = {
      url: document.location.href,
      title: props.title,
    };
    try {
      await navigator.share(data);
    } catch (error) {
      logError([error], 'BlogPostSingle → shareBlogPost');
    }
  };

  interface TableOfContentsItem {
    text: string;
    level: number;
    id: string;
    startsWithNumber: boolean;
  }

  const generateTableOfContents = (body: string): TableOfContentsItem[] => {
    const lines = body.split('\n');
    const headings: TableOfContentsItem[] = [];

    lines.forEach((line) => {
      const match = line.match(/^(#{1,2})\s+(.*)/); // # (h1) and ## (h2) only
      if (match) {
        const level = match[1].length;
        const text = match[2].trim();
        const id = text
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^\w-]/g, '');
        const startsWithNumber = /^\d/.test(text);
        headings.push({ text, level, id, startsWithNumber });
      }
    });

    return headings;
  };

  const scrollToHeadingByText = (text: string) => {
    const headings = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
    const normalizedText = text.trim().toLowerCase();
    const navBar = document.querySelector('[class^="Navbar_"]');

    let offset = 0;
    if (navBar) {
      offset = (navBar as HTMLElement).offsetHeight;
    } else {
      offset = 144;
    }

    for (const heading of Array.from(headings)) {
      if ((heading.textContent || '').trim().toLowerCase() === normalizedText) {
        const rect = heading.getBoundingClientRect();
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const top = rect.top + scrollTop - offset - 20;
        window.scrollTo({ top, behavior: 'smooth' });
        break;
      }
    }
  };

  return (
    <article className={BlogPostPageStyle.post}>
      <header className={BlogPostPageStyle['post-header']}>
        <div className={BlogPostPageStyle['post-thumbnail']}>
          <Image
            quality={100}
            fill
            sizes="(max-width: 768px) 100vw, 2000px"
            alt={props.image?.alternativeText || props.title}
            priority={true}
            src={
              getImageSrcWithFallback(props.image, 'xlarge') ||
              '/assets/images/blog/blog-placeholder-image.png'
            }
          />
        </div>
        <div className={BlogPostPageStyle['post-meta']}>
          <div className={BlogPostPageStyle['post-metadata']}>
            <p>
              <a
                href={`${props.authorPath}`}
                className={BlogPostPageStyle['post-metadata-author']}>
                {props.authorPrefix} {props.author?.fullname || 'iGerent'}
              </a>
              <time
                className={BlogPostPageStyle['post-metadata-date']}
                dateTime={formatDate(props.publicationDate, true)}>
                {props.publishedDatePrefix} {formatDate(props.publicationDate)}
              </time>
              <time
                className={BlogPostPageStyle['post-metadata-date']}
                dateTime={formatDate(props.updatedAt, true)}>
                {props.lastUpdatedLabel} {formatDate(props.updatedAt)}
              </time>
            </p>
          </div>
          <div
            className={BlogPostPageStyle['post-social-media']}
            onClick={shareBlogPost}>
            <Image
              quality={90}
              src="/assets/images/icons/FacebookIcon.svg"
              alt="Facebook"
              width={21}
              height={21}
            />
            <Image
              quality={90}
              src="/assets/images/icons/TwitterIcon.svg"
              alt="Twitter"
              width={21}
              height={21}
            />
            <Image
              quality={90}
              src="/assets/images/icons/LinkedinIcon.svg"
              alt="Linkedin"
              width={21}
              height={21}
            />
          </div>
        </div>

        {!!props.bodyTitle && props.bodyTitle !== 'n/a' && (
          <>
            <h1>{props.bodyTitle}</h1>
          </>
        )}

        {!!props.summary && props.summary !== 'n/a' && (
          <div className="page-content-block is-boxed">
            <ReactMarkdown>{props.summary}</ReactMarkdown>
          </div>
        )}
      </header>

      <div className={BlogPostPageStyle['post-table-of-contents']}>
        {props.tableOfContentsTitle && (
          <p className={BlogPostPageStyle['post-table-of-contents-title']}>
            {props.tableOfContentsTitle}
          </p>
        )}
        <ul>
          {generateTableOfContents(props.body).map((item, index) => (
            <li
              key={index}
              className={`${
                item.startsWithNumber
                  ? BlogPostPageStyle['post-table-of-contents-item-padded']
                  : ''
              }`}>
              <span onClick={() => scrollToHeadingByText(item.text)}>
                {item.text}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <section className={BlogPostPageStyle['post-content']}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            table: ({ node, ...props }) => (
              <div className={BlogPostPageStyle['post-content-table']}>
                <table {...props} />
              </div>
            ),
          }}>
          {props.content}
        </ReactMarkdown>
      </section>
    </article>
  );
};

export default BlogPostSingle;
